import { useState, useEffect } from 'react';
import { Users, Plus, Bell, Bookmark, Settings } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import PostList from '@/components/community/PostList';
import PostForm from '@/components/community/PostForm';
import NotificationBell from '@/components/community/NotificationBell';
import CommunitiesList from '@/components/community/CommunitiesList';
import { postsAPI } from '@/api/community';
import { getUserData } from '@/utils/auth';

export default function CommunityPage() {
  const [activeTab, setActiveTab] = useState('announcements');
  const [showPostForm, setShowPostForm] = useState(false);
  const [showSavedPosts, setShowSavedPosts] = useState(false);
  const [showCommunities, setShowCommunities] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const userData = getUserData('student') || getUserData('teacher') || getUserData('admin') || getUserData('guardian');
  const userType = userData?.user_type || 'student';
  const isGuardian = userType === 'guardian';
  const isStudent = userType === 'student';
  const isAdmin = userType === 'admin';
  const canPost = !isGuardian;

  const handlePostCreated = () => {
    setRefreshKey(prev => prev + 1);
    setShowPostForm(false);
  };

  const handlePostUpdate = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Fetch functions for each tab
  const fetchAnnouncements = (page, pageSize) => {
    return postsAPI.getAnnouncements(page, pageSize);
  };

  const fetchMyFeed = (page, pageSize) => {
    return postsAPI.getMyFeed(page, pageSize);
  };

  const fetchAllPosts = (page, pageSize) => {
    return postsAPI.getAllPosts(page, pageSize);
  };

  const fetchSavedPosts = (page, pageSize) => {
    return postsAPI.getSavedPosts(page, pageSize);
  };

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-3 sm:p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto w-full space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="relative flex flex-col items-center gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-3 sm:gap-4">
              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-[hsl(var(--green-bright))]" />
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">Community</h1>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              {isGuardian ? 'View community posts (read-only)' : 'Connect, share, and learn with your community'}
            </p>
          </div>
          <div className="flex flex-col items-center gap-2 lg:flex-row lg:items-center lg:justify-end lg:gap-2">
            <div className="absolute top-0 right-0 lg:static lg:relative">
              <NotificationBell />
            </div>
            {canPost && (
              <div className="flex justify-center gap-2 lg:flex-nowrap lg:gap-2 overflow-x-auto">
                <Button
                  variant="outline"
                  onClick={() => setShowSavedPosts(true)}
                  className="text-[hsl(var(--green-bright))] hover:text-[hsl(var(--teal-vivid))] text-xs sm:text-sm px-2 sm:px-3 py-2"
                  title="Saved Posts"
                >
                  <Bookmark className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Saved Posts</span>
                </Button>
                {(isStudent || isAdmin) && (
                  <Button
                    variant="outline"
                    onClick={() => setShowCommunities(true)}
                    className="text-[hsl(var(--green-bright))] hover:text-[hsl(var(--teal-vivid))] text-xs sm:text-sm px-2 sm:px-3 py-2"
                    title="Manage Communities"
                  >
                    <Settings className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Manage Communities</span>
                  </Button>
                )}
                <Button
                  onClick={() => setShowPostForm(true)}
                  className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] text-xs sm:text-sm px-2 sm:px-3 py-2"
                  title="New Post"
                >
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">New Post</span>
                </Button>
              </div>
            )}
            {isGuardian && (
              <Badge variant="secondary" className="text-xs">
                Read-only
              </Badge>
            )}
          </div>
        </div>

        {/* Three Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="announcements" className="text-xs sm:text-sm">Announcements</TabsTrigger>
            <TabsTrigger value="my-feed" className="text-xs sm:text-sm">My Feed</TabsTrigger>
            <TabsTrigger value="posts" className="text-xs sm:text-sm">Posts</TabsTrigger>
          </TabsList>

          <TabsContent value="announcements" className="mt-4">
            <PostList
              key={`announcements-${refreshKey}`}
              fetchFunction={fetchAnnouncements}
              onPostUpdate={handlePostUpdate}
              isGuardian={isGuardian}
            />
          </TabsContent>

          <TabsContent value="my-feed" className="mt-4">
            <PostList
              key={`my-feed-${refreshKey}`}
              fetchFunction={fetchMyFeed}
              onPostUpdate={handlePostUpdate}
              isGuardian={isGuardian}
            />
          </TabsContent>

          <TabsContent value="posts" className="mt-4">
            <PostList
              key={`posts-${refreshKey}`}
              fetchFunction={fetchAllPosts}
              onPostUpdate={handlePostUpdate}
              isGuardian={isGuardian}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Post Form Modal */}
      {canPost && (
        <PostForm
          isOpen={showPostForm}
          onClose={() => setShowPostForm(false)}
          onPostCreated={handlePostCreated}
          userType={userType}
        />
      )}

      {/* Saved Posts Modal */}
      {showSavedPosts && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSavedPosts(false)}>
          <div className="bg-[hsl(var(--card-bg))] rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 sm:p-6 border-b border-[hsl(var(--card-border))]">
              <h2 className="text-base sm:text-2xl font-bold text-foreground">Saved Posts</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSavedPosts(false)}
                className="text-xs sm:text-base"
              >
                ×
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-6">
              <PostList
                key={`saved-posts-${refreshKey}`}
                fetchFunction={fetchSavedPosts}
                onPostUpdate={handlePostUpdate}
                isGuardian={isGuardian}
              />
            </div>
          </div>
        </div>
      )}

      {/* Communities Management Modal */}
      {showCommunities && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCommunities(false)}>
          <div className="bg-[hsl(var(--card-bg))] rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 sm:p-6 border-b border-[hsl(var(--card-border))]">
              <h2 className="text-base sm:text-2xl font-bold text-foreground">Manage Communities</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowCommunities(false)}
                className="text-xs sm:text-base"
              >
                ×
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-6">
              <CommunitiesList
                isOpen={showCommunities}
                onClose={() => setShowCommunities(false)}
                isAdmin={isAdmin}
                isTeacher={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
